


import Component, { mixins } from "vue-class-component";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import {
  ContentFeed,
  ContentFeedSearchResult,
  ContentFeedTypeEnum,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import WithAuthUser from "@/mixins/WithAuthUser";
import WithContentFeed from "@/mixins/WithContentFeed";
import providers from "@/lib/api";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { TabField } from "@/types";
import { EVENTS } from "@/const";
import BasePage from "@/components/BasePage";
import ContentFeedList from "@/components/ContentFeedList";
import EmptyContent from "@/components/EmptyContent";
import StoriesLine from "@/components/StoriesLine";
import TabsHeader from "@/components/TabsHeader";
import { useAgeVerification } from "@/features/age-verification";

@Component({
  name: "PageFeed",
  components: { ContentFeedList, BasePage, EmptyContent, TabsHeader, StoriesLine },
  setup() {
    const { onVerified } = useAgeVerification();
    return {
      onVerified,
    };
  },
})
export default class PageFeed extends mixins(WithAuthUser, WithContentFeed) {
  public onVerified!: ReturnType<typeof useAgeVerification>["onVerified"];

  public tabs: TabField[] = [
    {
      name: "home",
      text: $t("Home"),
      to: { name: "home" },
    },
    {
      name: "discover",
      text: $t("Discover"),
      to: { name: "discover", params: { type: "all" } },
    },
  ];

  public contentFeedResponse: ContentFeedSearchResult | null = null;

  public scroll = false;

  public loading = false;

  public subscriptionLevels: SubscriptionLevel[] = [];

  public paymentPackages: PaymentPackage[] = [];

  get emptyFeed() {
    return !this.loading && this.contentFeedList.length === 0;
  }

  get contentFeedList(): ContentFeed[] {
    return this.contentFeedResponse?.result ?? [];
  }

  public async getFeed(page = 0, replace = true, feedIdToFind?: string) {
    try {
      this.scroll = false;
      const data = await this.contentFeedFollowingSearch(
        {
          followerId: this.authUserStrict.id,
          page,
          sort: "PUBLISH_TIME,DESC",
          types: [ContentFeedTypeEnum.MEDIA, ContentFeedTypeEnum.EVENT],
        },
        feedIdToFind
      );
      if (!replace && this.contentFeedResponse) {
        data.result = [...this.contentFeedResponse.result, ...data.result];
      }
      this.contentFeedResponse = data;
    } finally {
      this.scroll = this.contentFeedResponse?.hasNextPage ?? false;
    }
  }

  public onContentFeedUpdate(contentFeed: ContentFeed) {
    if (!this.contentFeedResponse) return;
    this.$set(
      this.contentFeedResponse,
      "result",
      this.contentFeedResponse.result.map(item => (item.id === contentFeed.id ? contentFeed : item))
    );
  }

  public deleteHandler(feedId: string) {
    if (!this.contentFeedResponse) return;
    this.$set(
      this.contentFeedResponse,
      "result",
      this.contentFeedResponse.result.filter(item => item.id !== feedId)
    );
  }

  public async getNext(cb: Function) {
    try {
      const nextPage = (this.contentFeedResponse?.page ?? 0) + 1;
      await this.getFeed(nextPage, false);
    } finally {
      cb();
    }
  }

  public async updateFeed(payload: { userId: string; feedId?: string }) {
    if (!this.contentFeedResponse || this.contentFeedResponse.result.length < 0) {
      return;
    }

    this.$set(
      this.contentFeedResponse,
      "result",
      await getPurchasedContentFeeds(
        { authorId: payload.userId, purchasedFeedId: payload.feedId },
        this.contentFeedResponse
      )
    );
  }

  public created() {
    this.loading = true;
    this.getFeed().finally(() => (this.loading = false));
    providers.SubscriptionLevelProvider.find({ userIds: [this.authUserStrict.id] }).then(
      r => (this.subscriptionLevels = r)
    );
    providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.FILESET] }).then(
      r => (this.paymentPackages = r)
    );

    this.onVerified(this.getFeed);
    emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, this.updateFeed);
    emitter.on<any>(EVENTS.CONTENT_FEED_UPDATED, this.updateFeed);
  }

  public destroyed() {
    emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, this.updateFeed);
    emitter.off<any>(EVENTS.CONTENT_FEED_UPDATED, this.updateFeed);
  }
}
